import React from "react";

import { render, screen, waitFor, fireEvent } from "@testing-library/react";
import { Provider } from "react-redux";
import { createStore } from "redux";

import { GestorApp } from "./GestorApp";
import { api } from "../../lib/api";

/* O painel do gestor montado como a TELA monta: GestorApp -> useContextoIE ->
   Redux. O teste de GestorPainel.test.jsx entrega as props na mão e por isso não
   alcança esta cadeia — foi o que deixou passar o defeito relatado pelo Kilson
   ("continuo sem conseguir clicar nesse ranking") mesmo com aquele arquivo
   verde.

   O estado do Redux aqui é o do usuário real de teste, medido em 04/08/2026:
   caio.mizan (id 414965), papel único 1 (Administrador Educandus), vínculo com
   group_id/region_id/school_id nulos — 33 de 33 vínculos de tipo G são assim. */

beforeAll(() => {
  global.IntersectionObserver = class {
    observe() {}

    unobserve() {}

    disconnect() {}
  };
  window.scrollTo = () => {};
});

jest.mock("../../lib/api", () => ({
  api: {
    professorFiltros: jest.fn(),
    gruposRanking: jest.fn(),
    regionalRanking: jest.fn(),
    kpisGlobal: jest.fn(),
    execucoesMensal: jest.fn(),
    faixas: jest.fn(),
    evolucao: jest.fn(),
    disciplinasDesempenho: jest.fn(),
    escolas: jest.fn()
  }
}));

jest.mock("../../components/charts/Charts", () => ({
  AreaChartCard: () => <div />,
  NotaBarraLista: () => <div />
}));
jest.mock("../../components/charts/EvolucaoTemporal", () => ({
  EvolucaoTemporal: () => <div />
}));
jest.mock("../../components/charts/FaixaKpiCompacta", () => ({
  FaixaKpiCompacta: () => <div />
}));

const REDES = [
  {
    id: 80,
    rede: "FORMANDO CIDADÃOS",
    escolas: 990,
    turmas: 9695,
    alunos: 141586,
    professores: 1011,
    nota_media: 7.2,
    execucoes: 0,
    tarefas_atribuidas: 86,
    inativos: 1677,
    nunca_acessaram: 139789,
    tempo_total_seg: 1431351,
    taxa_conclusao: 0,
    engajamento_medio: 9.4
  },
  {
    id: 92,
    rede: "Sucesso Digital",
    escolas: 424,
    turmas: 3817,
    alunos: 59844,
    professores: 500,
    nota_media: 7,
    execucoes: 10,
    tarefas_atribuidas: 100,
    inativos: 900,
    nunca_acessaram: 40000,
    tempo_total_seg: 100000,
    taxa_conclusao: 10,
    engajamento_medio: 6.7
  }
];

const KPIS = {
  regioes: 74,
  escolas: 1888,
  alunos: 207853,
  professores: 4171,
  turmas: 17030,
  tarefas_atribuidas: 2002,
  execucoes: 185,
  nota_media: 7.1,
  engajamento_medio: 19,
  tempo_total_seg: 4681184,
  inativos: 8117,
  nunca_acessaram: 244906,
  taxa_conclusao: 9.2,
  inativos_dias: 7
};

/** O Redux do AVA, no estado do Administrador Educandus. */
function lojaDo(activeRoles, userRoles) {
  return createStore(() => ({
    general: { active_roles: activeRoles },
    user: { user_roles: userRoles }
  }));
}

const EDUCANDUS_SEM_REDE = {
  role_id: 1,
  role_name: "Administrador Educandus",
  group_id: null,
  group_name: null,
  region_id: null,
  school_id: null,
  type: "G"
};

const VINCULO_EDUCANDUS = [
  {
    role_id: 1,
    group_id: null,
    region_id: null,
    school_id: null
  }
];

beforeEach(() => {
  jest.clearAllMocks();
  api.professorFiltros.mockResolvedValue({
    anos: [{ ano: 2026 }],
    grupos: [
      { id: 80, nome: "FORMANDO CIDADÃOS" },
      { id: 92, nome: "Sucesso Digital" }
    ],
    regionais: [{ id: 171, nome: "PE", region_id: 171 }],
    escolas: []
  });
  api.gruposRanking.mockResolvedValue(REDES);
  api.regionalRanking.mockResolvedValue([
    {
      id: 171,
      regional: "PE",
      escolas: 167,
      turmas: 1669,
      alunos: 26725,
      professores: 132,
      nota_media: 7.3,
      execucoes: 5,
      tarefas_atribuidas: 50,
      inativos: 100,
      nunca_acessaram: 1000,
      tempo_total_seg: 5000,
      taxa_conclusao: 10,
      engajamento_medio: 9
    }
  ]);
  api.kpisGlobal.mockResolvedValue(KPIS);
  api.execucoesMensal.mockResolvedValue([]);
  api.faixas.mockResolvedValue([]);
  api.evolucao.mockResolvedValue([]);
  api.disciplinasDesempenho.mockResolvedValue([]);
  api.escolas.mockResolvedValue({ total: 0, rows: [] });
});

function renderizar(
  activeRoles = EDUCANDUS_SEM_REDE,
  userRoles = VINCULO_EDUCANDUS
) {
  return render(
    <Provider store={lojaDo(activeRoles, userRoles)}>
      <GestorApp />
    </Provider>
  );
}

describe("Administrador Educandus abrindo o painel", () => {
  it("chega ao ranking de redes", async () => {
    renderizar();

    expect(await screen.findByText("Ranking de redes")).toBeInTheDocument();
    expect(await screen.findByText("FORMANDO CIDADÃOS")).toBeInTheDocument();
  });

  /** O relato: "continuo sem conseguir clicar nesse ranking". */
  it("CLICAR numa rede desce para as regiões dela", async () => {
    renderizar();

    fireEvent.click(await screen.findByText("FORMANDO CIDADÃOS"));

    expect(await screen.findByText("Ranking de regiões")).toBeInTheDocument();
    await waitFor(() => expect(api.regionalRanking).toHaveBeenCalledWith(80));
  });

  it("com uma rede escolhida no modal, abre nela e deixa subir", async () => {
    renderizar({
      ...EDUCANDUS_SEM_REDE,
      group_id: 80,
      group_name: "FORMANDO CIDADÃOS"
    });

    // Abre dentro da rede.
    expect(await screen.findByText("Ranking de regiões")).toBeInTheDocument();
    await waitFor(() => expect(api.regionalRanking).toHaveBeenCalledWith(80));

    // O breadcrumb tem o degrau de cima; clicar nele volta ao ranking de redes.
    fireEvent.click(await screen.findByText("Todas as redes"));

    expect(await screen.findByText("Ranking de redes")).toBeInTheDocument();
    expect(api.gruposRanking).toHaveBeenCalled();
  });
});
